import {
  PartnerName,
  useAccount,
  useAuthSendEmailVerification,
  useVerificationCode,
  useVexMyOrganization,
} from '@melio/platform-api';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import { NOT_FOUND_PAGE } from '../../shared';
import { CodeVerificationScreen, EmailLoginScreen } from './screens';

export type EmailVerificationActivityProps = {
  children?: React.ReactNode;
  setAccessTokens: (accessToken: string, refreshToken: string | null) => void;
  showPoweredByMelio?: boolean;
  helpLinkHref: string;
  defaultReturnUrl?: string;
};

const LOGIN_QUERY_PARAMS = { returnUrl: 'returnUrl', emailAddress: 'emailAddress' } as const;

export const EmailVerificationActivity: React.VFC<EmailVerificationActivityProps> = ({
  setAccessTokens,
  showPoweredByMelio,
  helpLinkHref,
  defaultReturnUrl = NOT_FOUND_PAGE,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnUrl = searchParams.get(LOGIN_QUERY_PARAMS.returnUrl);
  const emailAddress = searchParams.get(LOGIN_QUERY_PARAMS.emailAddress);

  /** @description Vendor user can be only melio's partner  */
  const partnerName = PartnerName.Melio;

  const backToEmailScreen = () => navigate('../email-verification');

  const {
    isLoading: isSendingEmail,
    error: sendEmailError,
    sendVerificationCode: sendVerificationEmailCode,
  } = useAuthSendEmailVerification();

  const { mutateAsync: submitVerificationCode } = useVerificationCode();
  const { invalidateQuery: invalidateAccountQuery } = useAccount({ id: 'me' });
  const { removeQuery: invalidateVexOrganizationQuery } = useVexMyOrganization();
  const onSendEmailVerificationCode = async (email: string) => {
    await sendVerificationEmailCode({
      email,
      partnerName,
      melioOnly: true,
    });

    const searchParams = new URLSearchParams({
      returnUrl: returnUrl || defaultReturnUrl,
      emailAddress: email,
    });

    navigate({
      pathname: 'code',
      search: searchParams.toString(),
    });
    return;
  };

  const onReSendEmailVerificationCode = (email: string) => {
    sendVerificationEmailCode({
      email,
      partnerName,
      melioOnly: true,
    }).catch(() => {
      backToEmailScreen();
    });
  };

  const onSubmitCodeVerification = async (code: string, email: string) => {
    const {
      data: { accessToken, refreshToken },
    } = await submitVerificationCode({ code: parseInt(code, 10), email, partnerName });
    setAccessTokens(accessToken, refreshToken ?? null);
    await invalidateAccountQuery({ id: 'me' });
    invalidateVexOrganizationQuery();
    navigate(returnUrl || defaultReturnUrl);
    return;
  };

  return (
    <Routes>
      <Route
        index
        element={
          <EmailLoginScreen
            onBack={() => navigate(returnUrl || defaultReturnUrl)}
            onDone={onSendEmailVerificationCode}
            showPoweredByMelio={showPoweredByMelio}
            isSaving={isSendingEmail}
            errorMessage={sendEmailError?.message}
          />
        }
      />
      <Route
        path="code"
        element={
          <CodeVerificationScreen
            generateNewCode={onReSendEmailVerificationCode}
            onBack={backToEmailScreen}
            onDone={onSubmitCodeVerification}
            email={emailAddress}
            helpLinkHref={helpLinkHref}
            showPoweredByMelio={showPoweredByMelio}
          />
        }
      />
    </Routes>
  );
};
